'use client';

/**
 * Service Referral Journey Component
 *
 * Visual timeline showing the complete service referral workflow:
 * Consent → SDOH Assessment → Provider Matching → Pricing → Service Delivery
 *
 * Displays current status and completion of each stage.
 */

import React from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  Shield,
  Heart,
  Users,
  DollarSign,
  Activity,
  FileText,
  ArrowRight,
  Calendar,
  Building2,
} from 'lucide-react';
import { ServiceReferral, ReferralStatus } from '@/services/api/serviceReferrals';

// ============================================================================
// TYPES
// ============================================================================

interface JourneyStage {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  status: 'completed' | 'current' | 'pending' | 'skipped';
  completedAt?: string;
  details?: string;
}

interface ServiceReferralJourneyProps {
  referral: ServiceReferral;
  consentInfo?: {
    accepted_at?: string;
    program_name?: string;
    signature_method?: string;
  };
  className?: string;
  compact?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getStageStatus(
  stageId: string,
  referralStatus: ReferralStatus,
  referral: ServiceReferral
): 'completed' | 'current' | 'pending' | 'skipped' {
  const statusFlow: Record<string, number> = {
    created: 0,
    pending_consent: 1,
    consent_obtained: 2,
    matching: 3,
    matched: 4,
    provider_notified: 5,
    provider_accepted: 6,
    provider_declined: 5,
    service_scheduled: 7,
    service_started: 8,
    service_completed: 9,
    billing_ready: 10,
    billed: 11,
    paid: 12,
    closed: 13,
    cancelled: -1,
    failed: -1,
  };

  const stageMapping: Record<string, number> = {
    consent: 2,
    sdoh: 3,
    matching: 4,
    pricing: 6,
    service: 8,
    completion: 9,
  };

  const currentStep = statusFlow[referralStatus] || 0;
  const stageStep = stageMapping[stageId] || 0;

  if (referralStatus === 'cancelled' || referralStatus === 'failed') {
    return currentStep >= stageStep ? 'completed' : 'pending';
  }

  if (currentStep > stageStep) return 'completed';
  if (currentStep === stageStep) return 'current';
  return 'pending';
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceReferralJourney({
  referral,
  consentInfo,
  className = '',
  compact = false,
}: ServiceReferralJourneyProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    return fmt.medium(dateStr);
  };

  const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    return fmt.dateTime(dateStr);
  };
  // Build journey stages based on referral data
  const stages: JourneyStage[] = [
    {
      id: 'consent',
      name: t('serviceReferrals.journey.consent', 'Consent'),
      icon: Shield,
      description: t('serviceReferrals.journey.consentDesc', 'Employee program consent'),
      status: referral.consent_obtained
        ? 'completed'
        : getStageStatus('consent', referral.status, referral),
      completedAt: referral.consent_date || consentInfo?.accepted_at,
      details: consentInfo?.program_name
        ? `${consentInfo.program_name}${consentInfo.signature_method ? ` (${consentInfo.signature_method})` : ''}`
        : referral.consent_obtained
        ? t('serviceReferrals.journey.consentObtained', 'Consent obtained')
        : undefined,
    },
    {
      id: 'sdoh',
      name: t('serviceReferrals.journey.sdohAssessment', 'SDOH Assessment'),
      icon: Heart,
      description: t('serviceReferrals.journey.sdohDesc', 'Social determinants evaluation'),
      status: referral.sdoh_assessment_id
        ? 'completed'
        : referral.sdoh_domain_code
        ? 'completed'
        : getStageStatus('sdoh', referral.status, referral),
      details: referral.sdoh_domain_code
        ? `${t('serviceReferrals.journey.primary', 'Primary')}: ${referral.sdoh_domain_code.replace(/_/g, ' ')}`
        : referral.sdoh_needs && referral.sdoh_needs.length > 0
        ? `${referral.sdoh_needs.length} ${t('serviceReferrals.journey.needsIdentified', 'needs identified')}`
        : undefined,
    },
    {
      id: 'matching',
      name: t('serviceReferrals.journey.providerMatching', 'Provider Matching'),
      icon: Users,
      description: t('serviceReferrals.journey.providerMatchingDesc', 'Find suitable providers'),
      status: referral.selected_provider_id
        ? 'completed'
        : getStageStatus('matching', referral.status, referral),
      details: referral.selected_provider_name
        ? `${t('serviceReferrals.journey.matched', 'Matched')}: ${referral.selected_provider_name}`
        : undefined,
    },
    {
      id: 'pricing',
      name: t('serviceReferrals.journey.pricingAgreement', 'Pricing & Agreement'),
      icon: DollarSign,
      description: t('serviceReferrals.journey.pricingDesc', 'Negotiate service terms'),
      status: referral.accepted_proposal_id
        ? 'completed'
        : referral.active_proposal_id
        ? 'current'
        : getStageStatus('pricing', referral.status, referral),
      details: referral.contracted_amount
        ? `${t('serviceReferrals.journey.agreed', 'Agreed')}: $${referral.contracted_amount.toLocaleString()}`
        : referral.active_proposal_id
        ? t('serviceReferrals.journey.proposalPending', 'Proposal pending')
        : undefined,
    },
    {
      id: 'service',
      name: t('serviceReferrals.journey.serviceDelivery', 'Service Delivery'),
      icon: Activity,
      description: t('serviceReferrals.journey.serviceDeliveryDesc', 'Active service in progress'),
      status: getStageStatus('service', referral.status, referral),
      completedAt: referral.actual_start_date,
      details: referral.actual_start_date
        ? `${t('serviceReferrals.journey.started', 'Started')} ${formatDate(referral.actual_start_date)}`
        : referral.requested_start_date
        ? `${t('serviceReferrals.journey.scheduled', 'Scheduled')} ${formatDate(referral.requested_start_date)}`
        : undefined,
    },
    {
      id: 'completion',
      name: t('serviceReferrals.journey.completion', 'Completion'),
      icon: CheckCircle,
      description: t('serviceReferrals.journey.completionDesc', 'Service completed & documented'),
      status: referral.status === 'service_completed' || referral.status === 'closed'
        ? 'completed'
        : getStageStatus('completion', referral.status, referral),
      completedAt: referral.actual_end_date,
      details: referral.outcome_status
        ? `${t('serviceReferrals.journey.outcome', 'Outcome')}: ${referral.outcome_status}`
        : undefined,
    },
  ];

  // Clinical<->social handoff: show the loop-back to the referring clinician once
  // a delivered outcome has been (or is due to be) pushed to the EHR. Only relevant
  // for clinically-originated referrals.
  if (referral.origin_clinical_trigger) {
    stages.push({
      id: 'clinician_loopback',
      name: t('serviceReferrals.journey.clinicianLoopback', 'Clinician notified'),
      icon: CheckCircle,
      description: t(
        'serviceReferrals.journey.clinicianLoopbackDesc',
        'Outcome sent back to the referring provider',
      ),
      status: referral.pcp_notification_sent
        ? 'completed'
        : referral.status === 'service_completed' || referral.status === 'closed'
          ? 'current'
          : 'pending',
      completedAt: referral.pcp_notified_at,
    });
  }

  // Compact view for embedding
  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {stages.map((stage, index) => {
          const Icon = stage.icon;
          return (
            <React.Fragment key={stage.id}>
              <div
                className={`
                  flex items-center justify-center w-6 h-6 rounded-full
                  ${stage.status === 'completed' ? 'bg-green-100 text-green-600' : ''}
                  ${stage.status === 'current' ? 'bg-blue-100 text-blue-600' : ''}
                  ${stage.status === 'pending' ? 'bg-gray-100 text-gray-400' : ''}
                  ${stage.status === 'skipped' ? 'bg-gray-100 text-gray-300' : ''}
                `}
                title={`${stage.name}: ${stage.status}`}
              >
                {stage.status === 'completed' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : stage.status === 'current' ? (
                  <Clock className="w-4 h-4" />
                ) : (
                  <Icon className="w-3 h-3" />
                )}
              </div>
              {index < stages.length - 1 && (
                <div
                  className={`w-4 h-0.5 ${
                    stage.status === 'completed' ? 'bg-green-300' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Full view
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('serviceReferrals.journey.title', 'Referral Journey')}
        </CardTitle>
        <CardDescription>
          {referral.referral_number} • {t('serviceReferrals.journey.created', 'Created')} {formatDate(referral.created_at)}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

          {/* Stages */}
          <div className="space-y-4">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              const isLast = index === stages.length - 1;

              return (
                <div key={stage.id} className="relative flex gap-4">
                  {/* Icon */}
                  <div
                    className={`
                      relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2
                      ${stage.status === 'completed' ? 'bg-green-50 border-green-500 text-green-600' : ''}
                      ${stage.status === 'current' ? 'bg-blue-50 border-blue-500 text-blue-600 ring-4 ring-blue-100' : ''}
                      ${stage.status === 'pending' ? 'bg-white border-gray-300 text-gray-400' : ''}
                      ${stage.status === 'skipped' ? 'bg-gray-50 border-gray-200 text-gray-300' : ''}
                    `}
                  >
                    {stage.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : stage.status === 'current' ? (
                      <Clock className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 pb-4 ${isLast ? 'pb-0' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          stage.status === 'pending' ? 'text-gray-400' : ''
                        }`}
                      >
                        {stage.name}
                      </span>
                      {stage.status === 'current' && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{t('serviceReferrals.journey.inProgress', 'In Progress')}</Badge>
                      )}
                      {stage.status === 'completed' && stage.completedAt && (
                        <span className="text-xs text-gray-500">
                          {formatDateTime(stage.completedAt)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{stage.description}</p>
                    {stage.details && (
                      <p className="text-sm text-gray-600 mt-1 font-medium">{stage.details}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Summary */}
        {(referral.status === 'cancelled' || referral.status === 'failed') && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">
                {referral.status === 'cancelled'
                  ? t('serviceReferrals.journey.referralCancelled', 'Referral Cancelled')
                  : t('serviceReferrals.journey.referralFailed', 'Referral Failed')}
              </span>
            </div>
            {referral.outcome_notes && (
              <p className="text-sm text-red-600 mt-1">{referral.outcome_notes}</p>
            )}
          </div>
        )}

        {referral.status === 'service_completed' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span className="font-medium">{t('serviceReferrals.journey.serviceCompleted', 'Service Completed Successfully')}</span>
            </div>
            {referral.satisfaction_score && (
              <p className="text-sm text-green-600 mt-1">
                {t('serviceReferrals.journey.satisfactionScore', 'Satisfaction Score')}: {referral.satisfaction_score}/5
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceReferralJourney;
